<script setup lang="ts">
    import { useTheme } from 'vuetify';
    import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
    import AppMenuAccount from '@/components/menu/AppMenuAccount.vue';
    import AppMenuAlerts from '@/components/menu/AppMenuAlerts.vue';
    import AppMenuDarkSwitcher from '@/components/menu/AppMenuDarkSwitcher.vue';
    import AppMenuNav from '@/components/menu/AppMenuNav.vue';
    import AppMenuSearch from '@/components/menu/AppMenuSearch.vue';
    import AppExpandableSearch from '@/components/search/AppExpandableSearch.vue';
    import { MENU_PAGE_ACTIONS_TARGET, MENU_PAGE_STATS_TARGET, usePageToolbarLayout } from '@/composables/usePageToolbarLayout';
    import { useScaledDisplay } from '@/composables/useScaledDisplay';
    import { useWindowResize } from '@/composables/useWindowResize';
    import { useMenuStore } from '@/stores/menu';
    import { useUserStore } from '@/stores/user';

    const userStore = useUserStore();
    const menuStore = useMenuStore();
    const scaledDisplay = useScaledDisplay();
    const theme = useTheme();

    const isDesktopMenu = computed<boolean>(() => {
        return scaledDisplay.mdAndUp.value;
    });

    const { isLandscapeMobile } = useWindowResize();
    const pageToolbarLayout = usePageToolbarLayout();

    const hasStats = computed(() => pageToolbarLayout?.hasStats.value ?? false);

    watch(isDesktopMenu, (value: boolean) => {
        if (value && menuStore.mobileMenuDrawer) {
            menuStore.mobileMenuDrawer = false;
        }
    });
</script>
<template>
    <v-card class="overflow-hidden">
        <v-app-bar id="menu" app dense flat tile :height="isLandscapeMobile ? 'auto' : '47'" class="menu-header">
            <v-toolbar-title v-show="scaledDisplay.lgAndUp.value" class="ml-3 logo-title">
                <router-link v-if="userStore.isLoggedIn()" :to="{ name: 'bets' }" class="link-plain logo-symbol">&lt;A/&gt;</router-link>
                <span v-else class="logo-symbol">&lt;A/&gt;</span>
            </v-toolbar-title>

            <AppMenuNav v-if="isDesktopMenu"></AppMenuNav>
            <AppMenuSearch v-if="scaledDisplay.lgAndUp.value" />

            <v-app-bar-nav-icon v-else class="ml-2 ml-md-0" @click="menuStore.mobileMenuDrawer = true">
                <v-icon :color="theme.name.value === 'dark' ? '#b5bac1' : 'tbGreen'" style="background: transparent !important" :icon="mdiMenu" />
            </v-app-bar-nav-icon>
            <v-spacer v-if="(isDesktopMenu || !isLandscapeMobile) && !hasStats"></v-spacer>

            <AppExpandableSearch v-if="!isLandscapeMobile" class="mr-2" :expand="isDesktopMenu ? 'left' : 'center'" />
            <AppBreadcrumbs class="mr-2" />
            <template v-if="!isLandscapeMobile || hasStats">
                <v-divider class="my-2" :class="isLandscapeMobile ? 'mr-3' : ''" vertical />
            </template>

            <template v-if="isLandscapeMobile">
                <div :id="MENU_PAGE_STATS_TARGET" class="d-flex align-center gap-2 flex-wrap min-width-0" />
                <v-spacer v-if="hasStats || !isDesktopMenu"></v-spacer>
                <v-divider v-else class="my-2 mr-3" vertical />
                <AppExpandableSearch />
                <div :id="MENU_PAGE_ACTIONS_TARGET" class="d-flex align-center" />
                <v-divider class="ml-4 my-2" vertical />
            </template>

            <AppMenuAlerts />
            <v-divider class="my-2" vertical />
            <AppMenuDarkSwitcher></AppMenuDarkSwitcher>

            <AppMenuAccount></AppMenuAccount>
        </v-app-bar>
    </v-card>
</template>
<style lang="scss" scoped>
    .menu-header {
        border: none !important;
        border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;

        :deep(.v-toolbar__content) {
            min-height: 47px;
        }
    }

    .logo-title {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        padding-top: 0 !important;
    }

    .logo-symbol {
        font-size: 1.25rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: rgb(var(--v-theme-tbGreen)) !important;
    }

    .min-width-0 {
        min-width: 0;
    }
</style>
